import { jsx as _jsx } from "react/jsx-runtime";
import { clsx } from "clsx";
import React, { useEffect, useRef, useState } from 'react';
const StateStyles = {
    LOADING: '--loading',
    SUCCESS: '--success',
    ERROR: '--error',
};
/**
 * Represents a HOC which wraps a BuyButton or ReorderButton and applies diffrent styles to it
 * depending on its click state.
 * @param {*} WrappedComponent The button component.
 * @param {*} onClick The async button's onClick event. It should return true if everything is OK, or false if there is any error.
 * @param {*} stylePrefix The style prefix to append state's modifiers. For
 * example, 'button' will result as 'button--loading'.
 *
 * Some available props that the HOC component supports:
 * autoReset : it is true by default. Not its value, but the behaviour is like that. Unless people set it as false, by default, button is always reset to neutral state after the request is completed.
 * resetTimeout: Number milisecond after the complete state, the style of button will be reset. If don't set, it is 2000
 * minimumLoadingTime: Mininum milisecond to display the loading state. If don't set, it is 1000
 */
export default function withReactiveStyleBuyButton(WrappedComponent, onClick, stylePrefix, buttonClassName) {
    return (props) => {
        const [stateClass, setStateClass] = useState('');
        const startTime = useRef(0);
        useEffect(() => {
            return () => {
                setStateClass('');
            };
        }, []);
        function onNeutralState() {
            setStateClass('');
            startTime.current = 0;
        }
        function onLoadingState() {
            setStateClass(`${stylePrefix}${StateStyles.LOADING}`);
            startTime.current = Date.now();
        }
        const changeState = (complete) => {
            setStateClass(`${stylePrefix}${complete ? StateStyles.SUCCESS : StateStyles.ERROR}`);
            const resetTimeout = props.successDisplayTime || props.resetTimeout || 2000;
            props.autoReset !== false &&
                setTimeout(onNeutralState, resetTimeout);
        };
        function onComplete(complete) {
            const loadingDuration = Date.now() - startTime.current;
            const minimumLoadingTime = props.spinnerMinimumTime || props.minimumLoadingTime || 1000;
            if (loadingDuration >= minimumLoadingTime) {
                changeState(complete);
            }
            else {
                setTimeout(() => changeState(complete), minimumLoadingTime - loadingDuration);
            }
        }
        async function onButtonClick(params) {
            onLoadingState();
            let data;
            try {
                data = await onClick(params);
            }
            catch (err) {
                data = false;
            }
            onComplete(data);
        }
        return (_jsx("span", { className: clsx(stateClass, 'reactive-button'), children: _jsx(WrappedComponent, { onClick: (params) => onButtonClick(params), ...props }) }));
    };
}
